/**
 * Document Lifecycle Policy
 *
 * Central source of truth for how finance document statuses transition.
 * Every status is either:
 *   - SYSTEM-managed: computed from payment/bank/date state (paid, partial,
 *     partially_paid, overdue, expired, converted). Users cannot set these
 *     manually.
 *   - USER-managed: deliberate human decisions exposed as named actions
 *     (send, cancel, void, accept, decline, approve, reject, close, receive).
 *
 * The server uses `isTransitionAllowed` + `computeSystemStatus` to guard
 * writes coming through /api/v1/finance, and the UI uses `getAvailableActions`
 * to render action buttons instead of a free status dropdown.
 *
 * Bank reconciliation (via bank_transactions → work_item_payments) is the
 * authoritative driver of paid/partial/overdue transitions for any document
 * that has an outstanding balance (invoice, expense, debit_note, supplier_credit
 * application).
 *
 * Refactored to use configLoader (types.yaml) as the primary source for
 * status definitions, user actions, and type metadata. Hardcoded data
 * is kept as fallback for when the registry is not yet initialized.
 */

import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';
import type { UserAction as ConfigUserAction } from '@/config/work-items/types';

export type FinanceDocType =
  | 'invoice' | 'quote' | 'quote_request' | 'credit_note' | 'debit_note'
  | 'proforma' | 'delivery_note' | 'waybill' | 'sales_order' | 'rma'
  | 'purchase_order' | 'grn' | 'supplier_credit' | 'supplier_quote'
  | 'purchase_requisition' | 'rfq' | 'purchase_return' | 'work_order'
  | 'picking_slip' | 'packing_slip' | 'stock_adjustment' | 'transfer_order'
  | 'billing_submission' | 'purchase' | 'expense' | 'fuel' | 'toll' | 'maintenance' | 'breakdown';

export interface DocumentLike {
  item_type?: string;
  doc_type?: string;
  status?: string | null;
  payment_status?: string | null;
  grand_total?: number | string | null;
  amount_paid?: number | string | null;
  balance_due?: number | string | null;
  due_date?: string | Date | null;
  expiry_date?: string | Date | null;
  sent_at?: string | Date | null;
  issued_at?: string | Date | null;
  start_date?: string | Date | null;
  converted_to_id?: number | string | null;
  [key: string]: unknown;
}

export interface UserAction {
  /** Internal id passed to /api/v1/finance performDocumentAction */
  id: string;
  /** Short label for buttons */
  label: string;
  /** Resulting status after the action */
  targetStatus: string;
  /** Visual tone hint for the UI */
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
  /** Optional confirm prompt */
  confirm?: string;
}

const num = (v: unknown): number => {
  const n = typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : 0;
  return Number.isFinite(n) ? n : 0;
};

const toDate = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = new Date(String(v));
  return isNaN(d.getTime()) ? null : d;
};

/** Statuses that are exclusively set by the system. */
export const SYSTEM_ONLY_STATUSES = new Set<string>([
  'paid',
  'partial',
  'partially_paid',
  'overdue',
  'expired',
  'converted',
]);

/** Terminal statuses that cannot be changed (except via explicit reinstate). */
export const TERMINAL_STATUSES = new Set<string>([
  'paid', 'void', 'cancelled', 'closed', 'converted', 'applied',
]);

/**
 * Statuses eligible for external (QBO/Xero/Sage) publishing.
 * 'paid' is included because COD expenses are born paid — payment is a
 * precondition of the document, not a later event — but they still need to
 * sync. Single source of truth for the publish crons and bulk-approval retry.
 */
export const PUBLISHABLE_STATUSES = new Set<string>([
  'ready', 'approved', 'ready_to_send', 'paid',
]);

/** True when a document status allows an outbound publish attempt. */
export function isPublishEligible(status: string | null | undefined): boolean {
  return PUBLISHABLE_STATUSES.has(String(status || '').toLowerCase());
}

// ---------------------------------------------------------------------------
// Per-doc_type action matrix
// Keyed by current status → list of allowed user actions.
// Anything not listed here is system-driven and NOT user-settable.
// ---------------------------------------------------------------------------

type ActionMatrix = Record<string, UserAction[]>;

const invoiceActions: ActionMatrix = {
  draft: [
    { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this draft invoice?' },
  ],
  sent: [
    { id: 'resend', label: 'Resend', targetStatus: 'sent', tone: 'neutral' },
    { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this invoice? This cannot be undone.' },
  ],
  partial: [
    { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void a partially-paid invoice? Payments must be unmatched first.' },
  ],
  overdue: [
    { id: 'resend', label: 'Send reminder', targetStatus: 'sent', tone: 'warning' },
    { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
  ],
};

const quoteActions: ActionMatrix = {
  draft: [
    { id: 'send', label: 'Send', targetStatus: 'sent', tone: 'primary' },
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
  sent: [
    { id: 'accept', label: 'Mark accepted', targetStatus: 'accepted', tone: 'success' },
    { id: 'decline', label: 'Mark declined', targetStatus: 'declined', tone: 'danger' },
    { id: 'resend', label: 'Resend', targetStatus: 'sent', tone: 'neutral' },
  ],
  accepted: [],
  declined: [
    { id: 'reopen', label: 'Re-open as draft', targetStatus: 'draft', tone: 'neutral' },
  ],
};

const expenseActions: ActionMatrix = {
  draft: [
    { id: 'approve', label: 'Approve for payment', targetStatus: 'received', tone: 'primary' },
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
  received: [
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
  partial: [
    { id: 'cancel', label: 'Cancel (unmatch payments first)', targetStatus: 'cancelled', tone: 'danger' },
  ],
  overdue: [
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
};

const purchaseOrderActions: ActionMatrix = {
  draft: [
    { id: 'send', label: 'Send to supplier', targetStatus: 'sent', tone: 'primary' },
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
  sent: [
    { id: 'accept', label: 'Mark accepted', targetStatus: 'accepted', tone: 'success' },
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
  accepted: [
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
};

const creditNoteActions: ActionMatrix = {
  draft: [
    { id: 'issue', label: 'Issue', targetStatus: 'sent', tone: 'primary' },
    { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
  ],
  sent: [
    { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this credit note?' },
  ],
  applied: [], // Terminal - no further actions
};

const debitNoteActions: ActionMatrix = {
  draft: [
    { id: 'issue', label: 'Issue', targetStatus: 'sent', tone: 'primary' },
    { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
  ],
  sent: [
    { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this debit note?' },
  ],
  acknowledged: [], // Terminal - no further actions
  applied: [], // Terminal - no further actions
};

const salesOrderActions: ActionMatrix = {
  draft: [
    { id: 'confirm', label: 'Confirm', targetStatus: 'confirmed', tone: 'primary' },
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
  confirmed: [
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
};

/** Fallback minimal matrix for doc_types we don't explicitly model. */
const genericActions: ActionMatrix = {
  draft: [
    { id: 'submit', label: 'Submit', targetStatus: 'pending', tone: 'primary' },
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
  pending: [
    { id: 'approve', label: 'Approve', targetStatus: 'approved', tone: 'success' },
    { id: 'cancel', label: 'Cancel', targetStatus: 'cancelled', tone: 'danger' },
  ],
  approved: [
    { id: 'close', label: 'Close', targetStatus: 'closed', tone: 'neutral' },
  ],
};

const ACTION_MATRIX_BY_DOC_TYPE: Partial<Record<string, ActionMatrix>> = {
  invoice: invoiceActions,
  proforma: quoteActions, // Proforma follows quote lifecycle (not payable)
  quote: quoteActions,
  quote_request: quoteActions,
  supplier_quote: quoteActions,
  supplier_credit: creditNoteActions,
  credit_note: creditNoteActions,
  debit_note: debitNoteActions,
  purchase_order: purchaseOrderActions,
  rfq: purchaseOrderActions,
  sales_order: salesOrderActions,
  billing_submission: {
    draft: [
      { id: 'submit', label: 'Submit for Approval', targetStatus: 'pending_approval', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    pending_approval: [
      { id: 'approve', label: 'Approve', targetStatus: 'approved', tone: 'success', confirm: 'Approve this billing submission?' },
      { id: 'reject', label: 'Return', targetStatus: 'rejected', tone: 'warning', confirm: 'Return this billing submission to operations?' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this billing submission?' },
    ],
    approved: [
      { id: 'generate_invoice', label: 'Generate Invoice', targetStatus: 'converted', tone: 'primary' },
      { id: 'generate_quote', label: 'Generate Quote', targetStatus: 'converted', tone: 'primary' },
      { id: 'generate_proforma', label: 'Generate Proforma', targetStatus: 'converted', tone: 'primary' },
      { id: 'reject', label: 'Return', targetStatus: 'rejected', tone: 'warning', confirm: 'Return this billing submission to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', targetStatus: 'pending_approval', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    converted: [], // Terminal - has been converted to finance document
    void: [], // Terminal
  },
  // Expenses and purchases share the same lifecycle
  purchase: {
    draft: [
      { id: 'submit', label: 'Submit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', targetStatus: 'approved', tone: 'success', confirm: 'Approve this expense?' },
      { id: 'reject', label: 'Reject', targetStatus: 'rejected', tone: 'warning', confirm: 'Reject this expense?' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', targetStatus: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', targetStatus: 'rejected', tone: 'warning', confirm: 'Return this expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    reimbursed: [], // Terminal
    void: [], // Terminal
  },
  expense: {
    draft: [
      { id: 'submit', label: 'Submit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', targetStatus: 'approved', tone: 'success', confirm: 'Approve this expense?' },
      { id: 'reject', label: 'Reject', targetStatus: 'rejected', tone: 'warning', confirm: 'Reject this expense?' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', targetStatus: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', targetStatus: 'rejected', tone: 'warning', confirm: 'Return this expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    reimbursed: [], // Terminal
    void: [], // Terminal
  },
  fuel: {
    draft: [
      { id: 'submit', label: 'Submit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', targetStatus: 'approved', tone: 'success', confirm: 'Approve this fuel expense?' },
      { id: 'reject', label: 'Reject', targetStatus: 'rejected', tone: 'warning', confirm: 'Reject this fuel expense?' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this fuel expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', targetStatus: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', targetStatus: 'rejected', tone: 'warning', confirm: 'Return this fuel expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    reimbursed: [], // Terminal
    void: [], // Terminal
  },
  toll: {
    draft: [
      { id: 'submit', label: 'Submit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', targetStatus: 'approved', tone: 'success', confirm: 'Approve this toll expense?' },
      { id: 'reject', label: 'Reject', targetStatus: 'rejected', tone: 'warning', confirm: 'Reject this toll expense?' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this toll expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', targetStatus: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', targetStatus: 'rejected', tone: 'warning', confirm: 'Return this toll expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    reimbursed: [], // Terminal
    void: [], // Terminal
  },
  maintenance: {
    draft: [
      { id: 'submit', label: 'Submit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', targetStatus: 'approved', tone: 'success', confirm: 'Approve this maintenance expense?' },
      { id: 'reject', label: 'Reject', targetStatus: 'rejected', tone: 'warning', confirm: 'Reject this maintenance expense?' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this maintenance expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', targetStatus: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', targetStatus: 'rejected', tone: 'warning', confirm: 'Return this maintenance expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    reimbursed: [], // Terminal
    void: [], // Terminal
  },
  breakdown: {
    draft: [
      { id: 'submit', label: 'Submit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    pending: [
      { id: 'approve', label: 'Approve', targetStatus: 'approved', tone: 'success', confirm: 'Approve this breakdown expense?' },
      { id: 'reject', label: 'Reject', targetStatus: 'rejected', tone: 'warning', confirm: 'Reject this breakdown expense?' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger', confirm: 'Void this breakdown expense?' },
    ],
    approved: [
      { id: 'reimburse', label: 'Mark Reimbursed', targetStatus: 'reimbursed', tone: 'primary' },
      { id: 'reject', label: 'Return', targetStatus: 'rejected', tone: 'warning', confirm: 'Return this breakdown expense to operations?' },
    ],
    rejected: [
      { id: 'submit', label: 'Resubmit', targetStatus: 'pending', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    reimbursed: [], // Terminal
    void: [], // Terminal
  },
  delivery_note: {
    draft: [
      { id: 'issue', label: 'Issue', targetStatus: 'sent', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    sent: [
      { id: 'mark_delivered', label: 'Mark Delivered', targetStatus: 'delivered', tone: 'success' },
    ],
    delivered: [], // Terminal
  },
  waybill: {
    draft: [
      { id: 'dispatch', label: 'Dispatch', targetStatus: 'in_transit', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    in_transit: [
      { id: 'mark_delivered', label: 'Mark Delivered', targetStatus: 'delivered', tone: 'success' },
      { id: 'mark_returned', label: 'Mark Returned', targetStatus: 'returned', tone: 'warning' },
    ],
    delivered: [], // Terminal
    returned: [], // Terminal
  },
  grn: {
    draft: [
      { id: 'receive', label: 'Mark Received', targetStatus: 'received', tone: 'primary' },
      { id: 'void', label: 'Void', targetStatus: 'void', tone: 'danger' },
    ],
    received: [
      { id: 'accept', label: 'Accept', targetStatus: 'accepted', tone: 'success' },
    ],
    accepted: [
      { id: 'close', label: 'Close', targetStatus: 'closed', tone: 'neutral' },
    ],
    closed: [], // Terminal
  },
};

/**
 * Convert a configLoader UserAction (snake_case target_status) to the
 * documentLifecycle UserAction (camelCase targetStatus) format.
 */
function adaptConfigAction(action: ConfigUserAction): UserAction {
  return {
    id: action.id,
    label: action.label,
    targetStatus: action.target_status,
    tone: action.tone,
    confirm: action.confirm,
  };
}

export function getActionMatrix(docType: string | undefined): ActionMatrix {
  const registry = getWorkItemTypeRegistry();
  const key = (docType || '').toLowerCase();

  // Try configLoader first (YAML-driven SSOT)
  if (registry.isInitialized()) {
    const entry = registry.getStatusDefinitionEntry(key);
    if (entry?.user_actions) {
      const matrix: ActionMatrix = {};
      for (const [status, actions] of Object.entries(entry.user_actions)) {
        matrix[status] = actions.map(adaptConfigAction);
      }
      return matrix;
    }
  }

  // Fallback to hardcoded matrix
  return ACTION_MATRIX_BY_DOC_TYPE[key] || genericActions;
}

/**
 * Returns the user actions available from the document's current state.
 * Empty array means the document is in a terminal / system-managed state
 * and the UI should show only the status badge.
 */
export function getAvailableActions(doc: DocumentLike): UserAction[] {
  const docType = String(doc.item_type || doc.doc_type || '').toLowerCase();
  const status = String(doc.status || 'draft').toLowerCase();

  // Try configLoader first (YAML-driven SSOT)
  const registry = getWorkItemTypeRegistry();
  if (registry.isInitialized()) {
    const configActions = registry.getUserActions(docType, status);
    if (configActions.length > 0) {
      return configActions.map(adaptConfigAction);
    }
    // If the entry exists but has no actions for this status, return empty
    // (this is correct for terminal statuses like 'paid', 'converted', etc.)
    const entry = registry.getStatusDefinitionEntry(docType);
    if (entry && entry.user_actions && docType !== '_generic') {
      return [];
    }
  }

  // Fallback to hardcoded matrix
  const matrix = getActionMatrix(docType);
  return matrix[status] ? [...matrix[status]] : [];
}

/**
 * True when the user is permitted to transition the document to `target`
 * from its current status through an exposed action.
 */
export function isTransitionAllowed(doc: DocumentLike, target: string): boolean {
  const normalizedTarget = String(target || '').toLowerCase();
  if (!normalizedTarget) return false;

  const docType = String(doc.item_type || doc.doc_type || '').toLowerCase();
  const currentStatus = String(doc.status || 'draft').toLowerCase();

  // Try configLoader first (YAML-driven SSOT)
  const registry = getWorkItemTypeRegistry();
  if (registry.isInitialized()) {
    // System-only statuses never come from user writes.
    if (registry.isSystemStatus(docType, normalizedTarget)) return false;
    return registry.isTransitionAllowed(docType, currentStatus, normalizedTarget);
  }

  // Fallback to hardcoded logic
  if (SYSTEM_ONLY_STATUSES.has(normalizedTarget)) return false;
  const available = getAvailableActions(doc);
  return available.some((action) => action.targetStatus.toLowerCase() === normalizedTarget);
}

/**
 * Compute the authoritative, system-managed status for a document based on
 * its balance, dates and current manual status. Returns the incoming status
 * unchanged when no system transition applies.
 *
 * Precedence:
 *   1. paid      — balance_due ≤ 0 and some amount has been paid
 *   2. partial   — amount_paid > 0 and balance_due > 0
 *   3. overdue   — unpaid invoice/expense past due_date
 *   4. expired   — quote/proforma past expiry_date, still unaccepted
 *   5. otherwise — leave current status as-is
 */
export function computeSystemStatus(
  doc: DocumentLike,
  nowInput: Date = new Date(),
): { status: string; payment_status: string | null; changed: boolean } {
  const docType = String(doc.item_type || doc.doc_type || '').toLowerCase();
  const currentStatus = String(doc.status || 'draft').toLowerCase();
  const currentPayment = doc.payment_status ? String(doc.payment_status).toLowerCase() : null;
  const now = nowInput;

  // Terminal statuses are locked — no system override.
  if (TERMINAL_STATUSES.has(currentStatus) && currentStatus !== 'paid') {
    return { status: currentStatus, payment_status: currentPayment, changed: false };
  }

  const grandTotal = num(doc.grand_total);
  const amountPaid = num(doc.amount_paid);
  const balanceDue = doc.balance_due !== null && doc.balance_due !== undefined
    ? num(doc.balance_due)
    : Math.max(0, grandTotal - amountPaid);

  const registry = getWorkItemTypeRegistry();
  const isPayable = registry.isInitialized()
    ? registry.isPayableType(docType)
    : ['invoice', 'expense'].includes(docType);

  // 1. Paid
  if (isPayable && grandTotal > 0 && balanceDue <= 0.005 && amountPaid > 0) {
    return { status: 'paid', payment_status: 'paid', changed: currentStatus !== 'paid' };
  }

  // 2. Partial
  if (isPayable && amountPaid > 0 && balanceDue > 0.005) {
    // Keep user-facing document status meaningful (sent/received) but flag payment_status.
    const keepStatus = ['draft', 'pending'].includes(currentStatus)
      ? (docType === 'expense' ? 'received' : 'sent')
      : currentStatus;
    return {
      status: keepStatus,
      payment_status: 'partial',
      changed: keepStatus !== currentStatus || currentPayment !== 'partial',
    };
  }

  // 3. Overdue
  if (isPayable && balanceDue > 0.005) {
    const due = toDate(doc.due_date);
    if (due && due.getTime() < now.getTime() && currentStatus !== 'draft') {
      return {
        status: 'overdue',
        payment_status: currentPayment || 'unpaid',
        changed: currentStatus !== 'overdue',
      };
    }
  }

  // 4. Expired (quotes/proformas/rfqs)
  const isQuotable = registry.isInitialized()
    ? registry.isQuotableType(docType)
    : ['quote', 'quote_request', 'proforma', 'rfq', 'supplier_quote'].includes(docType);
  if (isQuotable && !['accepted', 'declined', 'converted', 'cancelled'].includes(currentStatus)) {
    const exp = toDate(doc.expiry_date);
    if (exp && exp.getTime() < now.getTime() && currentStatus !== 'draft') {
      return {
        status: 'expired',
        payment_status: currentPayment,
        changed: currentStatus !== 'expired',
      };
    }
  }

  return { status: currentStatus, payment_status: currentPayment, changed: false };
}

/**
 * Enrich a document row before sending to the client so dashboards and the
 * document modal always see the system-computed status, even if the stored
 * row is slightly stale (e.g. overdue just passed midnight).
 */
export function enrichDocumentStatus<T extends DocumentLike>(doc: T, now: Date = new Date()): T {
  const { status, payment_status } = computeSystemStatus(doc, now);
  return { ...doc, status, payment_status } as T;
}

/**
 * Returns true when the given status should be shown in the UI as
 * "system-managed" (ie read-only, user cannot switch to/from it directly).
 */
export function isStatusSystemManaged(status: string | null | undefined): boolean {
  const normalized = String(status || '').toLowerCase();
  const registry = getWorkItemTypeRegistry();
  if (registry.isInitialized()) {
    // Check against all finance item types' system statuses
    for (const itemType of registry.getFinanceTypes()) {
      if (registry.isSystemStatus(itemType.type_key, normalized)) return true;
    }
    return false;
  }
  return SYSTEM_ONLY_STATUSES.has(normalized);
}
